import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon from "@insite/mobius/Icon";
import Package from "@insite/mobius/Icons/Package";
import LazyImage from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ClockIcon from "@crescentParts/Icons/ClockIcon";
import React, { FC } from "react";
import { css } from "styled-components";
import { CartSupportIcon } from "@crescentParts/Icons/CartSupportIcon";
import StarIcon from "@crescentParts/Icons/StarIcon";
import DollarIcon from "@crescentParts/Icons/DollarIcon";

const enum fields {
    supportItems = "supportItems",
    backgroundColor = "backgroundColor",
    borderRadius = "borderRadius",
}

const enum supportIcon {
    star = "star",
    dollar = "dollar",
    package = "package",
    clock = "clock",
}

const supportIconMap = {
    [supportIcon.star]: StarIcon,
    [supportIcon.dollar]: DollarIcon,
    [supportIcon.package]: Package,
    [supportIcon.clock]: ClockIcon,
};

interface SupportItem {
    fields: {
        icon: supportIcon;
        customIcon: string;
        text: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.supportItems]: SupportItem[];
        [fields.backgroundColor]: string;
        [fields.borderRadius]: string;
    };
}

export interface ProductDetailsSupportStyles {
    supportItemWrapper?: InjectableCss;
    customIcon?: InjectableCss;
    typographyText?: InjectableCss;
    mainWrapper?: InjectableCss;
    ProductDetailsSupportSection?: InjectableCss;
}

export const productDetailsSupportStyles: ProductDetailsSupportStyles = {
    supportItemWrapper: {
        css: css`
            font-style: normal;
            font-weight: 500;
            font-size: 12px;
            line-height: 22px;
            color: ${getColor("text.accent")};
            display: flex;
            margin-bottom: 13px;
            align-items: center;
        `,
    },
    typographyText: {
        css: css`
            font-size: 14px;
        `,
    },
    customIcon: {
        css: css`
            margin-right: 10px;

            img {
                width: 24px;
                height: 24px;
                object-fit: contain;
            }
        `,
    },
    mainWrapper: {
        css: css`
            width: 100%;
            padding: 18px 12px;
            border: 1px solid #D3D3D3;
            ${TypographyStyle}{
               margin-bottom: 10px;
            }
            ${TypographyStyle}:last-child {
                margin-bottom: 0px;
            }
        `,
    },
    ProductDetailsSupportSection:
    {
        css: css`
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 15px;
        justify-content: center;
        `
    }
};

const styles = productDetailsSupportStyles;

export const ProductDetailsSupport: FC<OwnProps> = ({ fields }) => {
    const supportItems = fields.supportItems || [];
    const mainWrapperStyle: InjectableCss = {
        css: css`
            ${styles.mainWrapper?.css}
            background: ${fields.backgroundColor || "#E9E9EA"};
            border-radius: ${fields.borderRadius || "4"}px;
        `,
    };

    return (
        <StyledWrapper {...styles.ProductDetailsSupportSection}>
        <StyledWrapper {...mainWrapperStyle}>
            {supportItems.map(({ fields: itemFields }) => {
                const customIcon = itemFields.customIcon;
                const IconSrc = supportIconMap[itemFields.icon];

                if (!itemFields.text || (!customIcon && !IconSrc)) {
                    return null;
                }

                return (
                    <StyledWrapper {...styles.supportItemWrapper} key={`${itemFields.icon}-${itemFields.text}`}>
                        {customIcon ? (
                            <LazyImage src={customIcon} altText="" {...styles.customIcon} />
                        ) : (
                            <Icon src={IconSrc} color="#000" style={{ marginRight: 10 }} />
                        )}
                        <Typography as="span" {...styles.typographyText}>
                            {itemFields.text}
                        </Typography>
                    </StyledWrapper>
                );
            })}
        </StyledWrapper>
        
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ProductDetailsSupport,
    definition: {
        group: "Basic",
        displayName: "Product details Cart Support",
        fieldDefinitions: [
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                fieldType: "General",
                editorTemplate: "ColorPickerField",
                defaultValue: "#E9E9EA",
            },
            {
                name: fields.borderRadius,
                displayName: "Border Radius",
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "4",
            },
            {
                name: fields.supportItems,
                displayName: "Support Items",
                fieldType: "General",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => item.fields.text || "Support Item",
                defaultValue: [
                    {
                        fields: {
                            icon: supportIcon.star,
                            text: "Authorized Distributor",
                        },
                    },
                    {
                        fields: {
                            icon: supportIcon.dollar,
                            text: "Net 30 Terms Available",
                        },
                    },
                ],
                fieldDefinitions: [
                    {
                        name: "icon",
                        displayName: "Icon",
                        editorTemplate: "DropDownField",
                        defaultValue: supportIcon.star,
                        options: [
                            { displayName: "Star", value: supportIcon.star },
                            { displayName: "Dollar", value: supportIcon.dollar },
                            { displayName: "Package", value: supportIcon.package },
                            { displayName: "Clock", value: supportIcon.clock },
                        ],
                    },
                    {
                        name: "customIcon",
                        displayName: "Custom Icon Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "text",
                        displayName: "Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
